import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { EVENT_TYPES } from '../../utils/constants';
import { validateEventName, validateEventDate, validateEventType, validateEmail } from '../../utils/validators';
import { createEvent, updateEvent } from '../../hooks/useEvents';
import { sendClientSetupEmail } from '../../services/emailService';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import LoadingSpinner from '../shared/LoadingSpinner';

function EventForm({ onClose, onSuccess, existingEvent = null }) {
  const { currentUser } = useAuth();
  const { showSuccess, showError, showInfo, removeNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    eventName: '',
    eventType: '',
    eventDate: '',
    location: '',
    hostName: '',
    clientEmail: '',
    guestPassword: '',
    welcomeMessage: '',
    thanksMessage: ''
  });

  const [formErrors, setFormErrors] = useState({});

  // Populate form if editing existing event
  useEffect(() => {
    if (existingEvent) {
      // Convert Firestore Timestamp to input date format (YYYY-MM-DD)
      const eventDate = existingEvent.eventDate?.toDate
        ? existingEvent.eventDate.toDate().toISOString().split('T')[0]
        : new Date(existingEvent.eventDate).toISOString().split('T')[0];

      setFormData({
        eventName: existingEvent.eventName || '',
        eventType: existingEvent.eventType || '',
        eventDate: eventDate,
        location: existingEvent.location || '',
        hostName: existingEvent.hostName || '',
        clientEmail: existingEvent.clientEmail || '',
        guestPassword: existingEvent.guestPassword || '',
        welcomeMessage: existingEvent.welcomeMessage || '',
        thanksMessage: existingEvent.thanksMessage || ''
      });
    }
  }, [existingEvent]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user types
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};

    const nameError = validateEventName(formData.eventName);
    if (nameError) errors.eventName = nameError;

    const typeError = validateEventType(formData.eventType);
    if (typeError) errors.eventType = typeError;

    const dateError = validateEventDate(formData.eventDate);
    if (dateError) errors.eventDate = dateError;

    if (!formData.location.trim()) {
      errors.location = 'Location is required';
    }

    if (!formData.hostName.trim()) {
      errors.hostName = 'Host name is required';
    }

    if (!formData.clientEmail.trim()) {
      errors.clientEmail = 'Client email is required';
    } else if (!validateEmail(formData.clientEmail.trim())) {
      errors.clientEmail = 'Please enter a valid email address';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const trackSetupEmailSend = (eventId, clientEmail) => {
    const infoId = showInfo(`Sending setup email to ${clientEmail}...`, { duration: null });

    sendClientSetupEmail(eventId)
      .then(result => {
        removeNotification(infoId);
        if (result.success) {
          showSuccess(`Setup email sent successfully to ${clientEmail}`);
        } else {
          showError(`Failed to send setup email to ${clientEmail}: ${result.error}`, {
            action: {
              label: 'Retry',
              onClick: () => trackSetupEmailSend(eventId, clientEmail),
            },
          });
        }
      })
      .catch(err => {
        removeNotification(infoId);
        showError(`Failed to send setup email: ${err.message}`, {
          action: {
            label: 'Retry',
            onClick: () => trackSetupEmailSend(eventId, clientEmail),
          },
        });
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Convert date string to Date object
      const eventDate = new Date(formData.eventDate);

      const eventData = {
        eventName: formData.eventName.trim(),
        eventType: formData.eventType,
        eventDate: eventDate,
        location: formData.location.trim(),
        hostName: formData.hostName.trim(),
        hostEmail: existingEvent?.hostEmail || currentUser?.email || '',
        clientEmail: formData.clientEmail.trim(),
        createdByEmail: existingEvent?.createdByEmail || currentUser?.email || '',
        guestPassword: formData.guestPassword.trim(),
        welcomeMessage: formData.welcomeMessage.trim(),
        thanksMessage: formData.thanksMessage.trim()
      };

      let result;
      if (existingEvent) {
        // Update existing event
        result = await updateEvent(existingEvent.id, eventData);
      } else {
        // Create new event
        result = await createEvent(eventData, currentUser.uid, currentUser.email);
      }

      if (result.success) {
        const newEventId = result.id;
        const clientEmail = formData.clientEmail.trim();

        onSuccess && onSuccess(newEventId);
        onClose();

        if (!existingEvent && newEventId && clientEmail) {
          trackSetupEmailSend(newEventId, clientEmail);
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-hidden">
      <div className="bg-[#1A1A1A] border border-neutral-700 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
        {/* Header */}
        <div className="sticky top-0 bg-[#1A1A1A] border-b border-neutral-700 px-6 py-4 flex justify-between items-center rounded-t-2xl">
          <h2 className="text-2xl font-bold text-white">
            {existingEvent ? 'Edit Event' : 'Create New Event'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-crimson/10 border border-crimson text-crimson px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Event Name */}
          <div>
            <label htmlFor="eventName" className="block text-sm font-medium text-white mb-2">
              Event Name *
            </label>
            <input
              type="text"
              id="eventName"
              name="eventName"
              value={formData.eventName}
              onChange={handleChange}
              className={`input-field w-full ${formErrors.eventName ? 'border-crimson' : ''}`}
              placeholder="Sarah & John's Wedding"
            />
            {formErrors.eventName && (
              <p className="mt-1 text-sm text-crimson">{formErrors.eventName}</p>
            )}
          </div>

          {/* Event Type */}
          <div>
            <label htmlFor="eventType" className="block text-sm font-medium text-white mb-2">
              Event Type *
            </label>
            <select
              id="eventType"
              name="eventType"
              value={formData.eventType}
              onChange={handleChange}
              className={`input-field w-full ${formErrors.eventType ? 'border-crimson' : ''}`}
            >
              <option value="">Select event type</option>
              {EVENT_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {formErrors.eventType && (
              <p className="mt-1 text-sm text-crimson">{formErrors.eventType}</p>
            )}
          </div>

          {/* Date and Location Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Event Date */}
            <div>
              <label htmlFor="eventDate" className="block text-sm font-medium text-white mb-2">
                Event Date *
              </label>
              <input
                type="date"
                id="eventDate"
                name="eventDate"
                value={formData.eventDate}
                onChange={handleChange}
                className={`input-field w-full ${formErrors.eventDate ? 'border-crimson' : ''}`}
              />
              {formErrors.eventDate && (
                <p className="mt-1 text-sm text-crimson">{formErrors.eventDate}</p>
              )}
            </div>

            {/* Location */}
            <div>
              <label htmlFor="location" className="block text-sm font-medium text-white mb-2">
                Location *
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className={`input-field w-full ${formErrors.location ? 'border-crimson' : ''}`}
                placeholder="The Grand Ballroom"
              />
              {formErrors.location && (
                <p className="mt-1 text-sm text-crimson">{formErrors.location}</p>
              )}
            </div>
          </div>

          {/* Host Name */}
          <div>
            <label htmlFor="hostName" className="block text-sm font-medium text-white mb-2">
              Host Name *
            </label>
            <input
              type="text"
              id="hostName"
              name="hostName"
              value={formData.hostName}
              onChange={handleChange}
              className={`input-field w-full ${formErrors.hostName ? 'border-crimson' : ''}`}
              placeholder="Sarah & John"
            />
            {formErrors.hostName && (
              <p className="mt-1 text-sm text-crimson">{formErrors.hostName}</p>
            )}
          </div>

          {/* Client Email */}
          <div>
            <label htmlFor="clientEmail" className="block text-sm font-medium text-white mb-2">
              Client Email *
            </label>
            <input
              type="email"
              id="clientEmail"
              name="clientEmail"
              value={formData.clientEmail}
              onChange={handleChange}
              className={`input-field w-full ${formErrors.clientEmail ? 'border-crimson' : ''}`}
              placeholder="client@example.com"
            />
            {formErrors.clientEmail && (
              <p className="mt-1 text-sm text-crimson">{formErrors.clientEmail}</p>
            )}
            <p className="mt-1 text-sm text-neutral-400">
              Client will receive an email to set up gallery access
            </p>
          </div>

          {/* Guest Password */}
          <div>
            <label htmlFor="guestPassword" className="block text-sm font-medium text-white mb-2">
              Guest Gallery Password (Optional)
            </label>
            <input
              type="text"
              id="guestPassword"
              name="guestPassword"
              value={formData.guestPassword}
              onChange={handleChange}
              className="input-field w-full"
              placeholder="Leave blank for public gallery"
            />
            <p className="mt-1 text-sm text-neutral-400">
              If set, guests will need this password to view the gallery
            </p>
          </div>

          {/* Welcome Message */}
          <div>
            <label htmlFor="welcomeMessage" className="block text-sm font-medium text-white mb-2">
              Welcome Message (Optional)
            </label>
            <textarea
              id="welcomeMessage"
              name="welcomeMessage"
              value={formData.welcomeMessage}
              onChange={handleChange}
              rows="3"
              className="input-field w-full resize-none"
              placeholder="Welcome! Share your favorite memory or well-wishes..."
            />
          </div>

          {/* Thanks Message */}
          <div>
            <label htmlFor="thanksMessage" className="block text-sm font-medium text-white mb-2">
              Thank You Message (Optional)
            </label>
            <textarea
              id="thanksMessage"
              name="thanksMessage"
              value={formData.thanksMessage}
              onChange={handleChange}
              rows="3"
              className="input-field w-full resize-none"
              placeholder="Thank you for sharing your message!"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-neutral-700 text-white font-semibold rounded-lg hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <LoadingSpinner size="small" />
                  {existingEvent ? 'Updating...' : 'Creating...'}
                </span>
              ) : (
                existingEvent ? 'Update Event' : 'Create Event'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EventForm;
